<template>
    <div class="list-item">
        <div class="inner-header">
            <PrismicImage
                w="75"
                h="75"
                :img="author.data.image"
                :alt="author.data.name"
            />
            <div class="header-name-role">
                <component
                    :is="authorLinkComponent"
                    v-bind="authorLinkProps"
                    class="author-name"
                >
                    {{ author.data.name }}
                </component>
                <p>{{ author.data.role }}</p>
            </div>
        </div>
        <AuthorSocials :author-document="author" />
        <div class="bio rich-text" v-html="authorBio" />

        <div v-if="tooltipItems.length && showTooltips" class="inner-tooltips">
            <TooltipHtml 
                v-for="(item, index) in tooltipItems" 
                :key="index" 
                :text="item.text"
            >
                <span class="tooltip-button-text">
                    {{ item.buttonText }}
                </span>
                <prismic-image 
                    v-if="item.iconImage && item.iconImage.url"
                    :img="item.iconImage"
                    w="20"
                    class="tooltip-image-icon"
                />
                <i 
                    v-else
                    class="fa-solid icon" 
                    :class="item.icon"
                />
            </TooltipHtml>
        </div>

        <div
            v-if="showArticles && articles.length"
            class="inner-list"
        >
            <span>
                {{ 
                    $translate('author_latest_articles_of_author', 'Senaste artiklarna av {author}')
                        .replace('{author}', author.data.name) 
                }} 
            </span>
            <ul>
                <li
                    v-for="(article, index) in articles"
                    :key="index"
                >
                    <nuxt-link :to="$prismic.linkResolver(article)">{{ article.data.title }}</nuxt-link>
                </li>
            </ul>
        </div>

        <div v-if="showSources" class="inner-list">
            <span>{{ $translate('author_sources', 'Källor') }}</span>
            <ul>
                <li v-for="source in sources" :key="source">
                    <a 
                        :href="source" 
                        target="_blank" 
                        rel="nofollow noopener noreferrer"
                    >
                        {{ source }}
                    </a>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { replace } from '@swegaming-ab/nuxtjs-helpers';
import TooltipHtml from '@/components/features/TooltipHtml.vue';
import AuthorSocials from '@/components/AuthorSocials.vue';
export default {
    components: {
        TooltipHtml,
        AuthorSocials
    },
    props: {
        id: {
            type: String,
            required: true
        },
        showArticles: {
            type: Boolean,
            required: false,
            default: true
        },
        sources: {
            type: Array,
            required: false,
            default: () => []
        },
        showTooltips: {
            type: Boolean,
            required: false,
            default: true
        },
        bioLong: {
            type: Array,
            required: false,
            default: null
        },
        isAuthorPage: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            articles: []
        };
    },
    async fetch() {
        const response = await this.$prismic.api.query(
            [
                this.$prismic.predicates.at('document.type', 'post'),
                this.$prismic.predicates.at('my.post.author', this.id) 
            ],
            {
                lang: process.env.PRISMIC_LOCALE,
                orderings: '[ document.first_publication_date desc ]',
                pageSize: 3
            }
        );
        this.articles = replace(response.results);
    },
    fetchKey() {
        return `3-author-${this.id}-posts`;
    },
    computed: {
        author() {
            return this.$store.state.authors[ this.id ];
        },
        showSources() {
            return this.sources.length > 0;
        },
        tooltipItems() {
            const settings = this.$store.state.settings;
            const verifiedInfoText = this.$prismic.asHtml(settings.verified_info_text);
            const howWeOperateText = this.$prismic.asHtml(settings.how_we_operate_text);
            return [
                {
                    icon: 'fa-solid fa-info-circle',
                    text: this.$validateText(howWeOperateText) ? howWeOperateText : null,
                    buttonText: settings.how_we_operate_button_text,
                },
                {
                    icon: 'fa-solid fa-badge-check',
                    text: this.$validateText(verifiedInfoText) ? verifiedInfoText : null,
                    buttonText: settings.verified_info_button_text,
                    iconImage: settings.verified_info_icon_image,
                },
            ].filter(item => item.text);
        },
        authorBio() {
            return this.isAuthorPage && this.$validateText(this.bioLong) 
                ? this.$prismic.asHtml(this.bioLong) 
                : this.author.data.bio;
        },
        authorLinkComponent() {
            return this.isAuthorPage 
                ? 'span' 
                : 'nuxt-link';
        },
        authorLinkProps() {
            return this.isAuthorPage
                ? {}
                : { to: this.$prismic.linkResolver(this.author) };
        }
    }
};
</script>

<style lang="scss" scoped>
    .list-item::v-deep {
        .bio { 
            @include font-size(14px); 
            * {
                &:first-child {
                    margin-top: 0;
                }
                @include font-size(14px); 
            }
        }

        img {
            box-shadow: 0 2px 5px -2px rgba(0,0,0,.14);
            border-radius: 3px;
        }

        flex: 1;
        @include grid(1, 20px);
        width: 100%;

        .inner-header {
            @include flex(start);
            gap: 1rem;

            .header-name-role {
                display: flex;
                flex-direction: column;
                gap: 0.5rem;
            }

            .author-name {
                font-size: 20px;
                text-decoration: none;
                color: $green;
                font-family: $fontHeader;
            }

            p {
                font-size: 16px;
            }
        }

        .inner-tooltips {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            column-gap: 1rem;

            .tooltip-container::v-deep {
                .tooltip-wrapper {
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: $cream;
                    border-radius: 5px;
                    padding: 8px 16px;
                    font-size: 14px;
                    cursor: pointer;
    
                    &:hover {
                        .tooltip-button-text {
                            text-decoration: none;
                        }
                    }
    
                    .tooltip-button-text {
                        line-height: 1.4;
                        text-decoration: underline;
                    }
    
                    .tooltip-image-icon {
                        max-width: 100%;
                    }
    
                    .icon {
                        color: $blue;
                    }
                }
            }
        }

        .inner-list {
            span {
                font-weight: bold;
                margin-bottom: 10px;
                display: block;
            }

            ul {
                @include grid(1, 10px);

                li {
                    font-size: 14px;

                    a {
                        color: $green;
                        text-decoration: none;
                        word-break: break-word;
                    }

                    a:hover { text-decoration: underline; }
                }
            }
        }
    }
</style>
